import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, FlatList, RefreshControl, Modal, TouchableOpacity, Alert, Platform } from 'react-native';
import { Card, Text, Title, Button, ActivityIndicator, Divider, useTheme, Badge, IconButton } from 'react-native-paper';
import { useFocusEffect } from '@react-navigation/native';
import { getMyActiveActivities, completeActivity } from '../../api/activitiesAPI';
import { formatDateTime, formatTime, getElapsedTime } from '../../utils/dateUtils';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import EmptyState from '../../components/EmptyState';
import { savePaginationState, loadPaginationState } from '../../utils/paginationUtils';

const SCREEN_KEY = 'user_active_park_activities_screen';

const ActiveParkActivitiesScreen = ({ navigation }) => {
  const theme = useTheme();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [completingActivity, setCompletingActivity] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  
  // Pagination state'ini localStorage'dan yükle
  const savedPagination = Platform.OS === 'web' ? 
    loadPaginationState(SCREEN_KEY, 1, 5) : 
    { currentPage: 1, itemsPerPage: 5 };
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(savedPagination.currentPage);
  const [itemsPerPage, setItemsPerPage] = useState(savedPagination.itemsPerPage);
  
  // Pagination state değiştiğinde localStorage'a kaydet (web için)
  useEffect(() => {
    if (Platform.OS === 'web') {
      savePaginationState(SCREEN_KEY, currentPage, itemsPerPage);
    }
  }, [currentPage, itemsPerPage]);

  // Aktif aktiviteleri yenile
  const fetchActivities = async () => {
    try {
      setError(null);
      const response = await getMyActiveActivities();

      if (response.success && response.data) {
        // API yanıtını loglayalım
        //console.log('API Aktiviteleri:', JSON.stringify(response.data, null, 2));
        
        // Süre hesaplamalarını ekleyerek aktiviteleri işle
        const activitiesWithDuration = response.data.map(activity => {
          //console.log('Aktivite detayı:', activity.id, 'Activity Type:', activity.activityType);
          return {
            ...activity,
            elapsed: getElapsedTime(activity.start_time)
          };
        });

        setActivities(activitiesWithDuration);
      } else {
        setError('Aktiviteler yüklenemedi');
      }
    } catch (err) {
      console.error('Aktiviteleri getirirken hata:', err);
      setError('Aktiviteleri yüklerken bir hata oluştu.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Aktivite bitir
  const handleCompleteActivity = async (activityId) => {
    try {
      setCompletingActivity(true);
      const response = await completeActivity(activityId);

      if (response.success) {
        Alert.alert(
          'Başarılı',
          'Oyun parkı çıkışı başarıyla yapıldı.',
          [
            { 
              text: 'Geçmişi Görüntüle', 
              onPress: () => navigation.navigate('ParkHistory') 
            },
            { 
              text: 'Tamam', 
              onPress: () => fetchActivities()
            }
          ]
        );
      } else {
        Alert.alert(
          'Hata',
          'Aktivite tamamlanamadı: ' + (response.message || 'Bilinmeyen hata'),
          [{ text: 'Tamam' }]
        );
      }
    } catch (error) {
      console.error('Aktivite tamamlama hatası:', error);
      Alert.alert(
        'Hata',
        'Aktivite tamamlanamadı: ' + (error.message || 'Bilinmeyen hata'),
        [{ text: 'Tamam' }]
      );
    } finally {
      setCompletingActivity(false);
      setShowModal(false);
    }
  };

  // Aktivite tamamlama onayı
  const confirmCompleteActivity = (activity) => {
    setSelectedActivity(activity);
    setShowModal(true);
  };

  // Süreyi her saniye güncelle
  useEffect(() => {
    if (activities.length > 0) {
      const interval = setInterval(() => {
        const updatedActivities = activities.map(activity => ({
          ...activity,
          elapsed: getElapsedTime(activity.start_time)
        }));
        setActivities(updatedActivities);
      }, 60000); // Her dakika güncelle

      return () => clearInterval(interval);
    }
  }, [activities]);

  // Sayfa odaklandığında aktiviteleri yükle
  useFocusEffect(
    useCallback(() => {
      fetchActivities();

      return () => {
        // Temizleme işlemleri (gerekirse)
      };
    }, [])
  );

  const onRefresh = () => {
    setRefreshing(true);
    fetchActivities();
    setCurrentPage(1); // Yenileme sırasında ilk sayfaya dön
  };

  // Pagination hesaplamaları
  const totalPages = Math.ceil(activities.length / itemsPerPage);
  
  // Mevcut sayfada gösterilecek aktiviteler
  const paginatedActivities = activities.slice(
    (currentPage - 1) * itemsPerPage, 
    currentPage * itemsPerPage
  );
  
  // Önceki sayfaya geç
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  // Sonraki sayfaya geç
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  // Sayfa numaralarını göster
  const renderPagination = () => {
    // Tüm durumlarda pagination'ı göster  
    return (
      <View style={styles.paginationContainer}>
        <IconButton
          icon="chevron-left"
          size={24}
          onPress={goToPreviousPage}
          disabled={currentPage === 1}
          style={[styles.paginationButton, currentPage === 1 ? styles.paginationButtonDisabled : null]}
          iconColor="rgb(125, 76, 236)"
        />
        
        <Text style={styles.paginationText}>
          Sayfa {currentPage} / {Math.max(totalPages, 1)} ({activities.length} kayıt)
        </Text>
        
        <IconButton
          icon="chevron-right"
          size={24}
          onPress={goToNextPage}
          disabled={currentPage === Math.ceil(activities.length / itemsPerPage)}
          style={[styles.paginationButton, currentPage === Math.ceil(activities.length / itemsPerPage) ? styles.paginationButtonDisabled : null]}
          iconColor="rgb(125, 76, 236)"
        />
      </View>
    );
  };

  // Aktivite kartını render et
  const renderActivityItem = ({ item: activity }) => {
    const childName = activity.child ? 
      `${activity.child.first_name} ${activity.child.last_name}` : 
      'Bilinmeyen Çocuk';
    const activityTypeName = activity.activityType?.name || 'Bilinmeyen Aktivite';
    const startTime = formatDateTime(activity.start_time);
    const startHour = formatTime(activity.start_time);

    // Ücret hesaplama (oyun parkı için)
    const calculateEstimatedPrice = (activity) => {
      //console.log('Activity için price hesaplanıyor:', activity.id);
      
      if (!activity.activityType) {
        //console.log('ActivityType bulunamadı!');
        return '0.00 TL';
      }
      
      //console.log('ActivityType değerleri:', 
      //  'base_price:', activity.activityType.base_price, 
      // 'price_per_minute:', activity.activityType.price_per_minute);
      
      // API'den price_per_minute gelmiyorsa manuel değer atayalım
      const basePrice = parseFloat(activity.activityType.base_price || 0);
      const pricePerMinute = activity.activityType.price_per_minute !== undefined ? 
        parseFloat(activity.activityType.price_per_minute) : 3; // Varsayılan değer olarak 3 atıyoruz
      const minutes = activity.elapsed ? activity.elapsed.minutes : 0;
      
      //console.log('Hesaplama değerleri:', 
      //  'basePrice:', basePrice, 
      //  'pricePerMinute:', pricePerMinute, 
      //  'minutes:', minutes);

      
      // Giriş ücreti + (dakika * dakika ücreti)
      const totalPrice = basePrice + (minutes * pricePerMinute);
      //console.log('Hesaplanan toplam ücret:', totalPrice);
      
      return `${totalPrice.toFixed(2)} TL`;
    };

    return (
      <Card style={styles.activityCard}>
        <Card.Content>
          <View style={styles.activityHeader}>
            <View style={styles.nameContainer}>
              <Title style={styles.childName}>{childName}</Title>
              <Badge style={styles.activeBadge}>Aktif</Badge>
            </View>
            <Text style={styles.activityType}>{activityTypeName}</Text>
          </View>

          <Divider style={styles.divider} />

          <View style={styles.infoContainer}>
            <View style={styles.infoItem}>
              <MaterialCommunityIcons name="clock-start" size={20} color={theme.colors.primary} />
              <Text style={styles.infoText}>Başlangıç: {startTime}</Text>
            </View>

            <View style={styles.infoItem}>
              <MaterialCommunityIcons name="calendar-clock" size={20} color="rgb(125, 76, 236)" />
              <Text style={styles.infoText}>Giriş: {formatDateTime(activity.start_time)}</Text>
            </View>

            <View style={styles.infoItem}>
              <MaterialCommunityIcons name="timer" size={20} color="#FF9800" />
              <Text style={styles.infoText}>Süre: {activity.elapsed.display}</Text>
            </View>

            <View style={styles.infoItem}>
              <MaterialCommunityIcons name="currency-try" size={20} color="#4CAF50" />
              <Text style={styles.infoText}>
                Ücret: {calculateEstimatedPrice(activity)}
              </Text>
            </View>
          </View>

        </Card.Content>
      </Card>
    );
  };

  return (
    <View style={styles.container}>
      {/* Onay Modalı */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={showModal}
        onRequestClose={() => setShowModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Oyun Parkı Çıkışı</Text>
            </View>

            <View style={styles.modalBody}>
              <Text style={styles.modalText}>
                {selectedActivity?.child?.first_name || 'Çocuk'} için oyun parkı ziyaretini sonlandırmak istediğinize emin misiniz?
              </Text>
            </View>

            <View style={styles.modalFooter}>
              <TouchableOpacity 
                style={[styles.modalButton, styles.cancelButton]} 
                onPress={() => setShowModal(false)}
              >
                <Text style={styles.cancelButtonText}>İptal</Text>
              </TouchableOpacity>

              <TouchableOpacity 
                style={[styles.modalButton, styles.confirmButton]} 
                onPress={() => handleCompleteActivity(selectedActivity?.id)}
                disabled={completingActivity}
              >
                <Text style={styles.confirmButtonText}>
                  {completingActivity ? 'İşleniyor...' : 'Çıkış Yap'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {loading && !refreshing ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator animating={true} color="rgb(125, 76, 236)" size="large" />
          <Text style={styles.loadingText}>Aktiviteler yükleniyor...</Text>
        </View>
      ) : (
        <>
          {error ? (
            <View style={styles.errorContainer}>
              <MaterialCommunityIcons 
                name="close-circle" 
                size={50} 
                color="rgb(125, 76, 236)" 
              />
              <Text style={styles.errorText}>{error}</Text>
              <Button mode="contained" onPress={fetchActivities} style={styles.retryButton}>
                Tekrar Dene
              </Button>
            </View>
          ) : activities.length === 0 ? (
            <EmptyState
              icon="clock-time-four"
              title="Aktif Ziyaret Bulunamadı"
              description="Şu anda aktif bir park ziyareti bulunmuyor."
              buttonTitle="Ziyaret Oluştur"
              buttonColor="rgb(125, 76, 236)"
              iconColor="rgb(125, 76, 236)"
              titleColor="rgb(125, 76, 236)"
              descriptionColor="rgb(90, 50, 180)"
              onButtonPress={() => navigation.navigate('ParkEntry')}
            />
          ) : (
            <>
              <FlatList
                data={paginatedActivities}
                renderItem={renderActivityItem}
                keyExtractor={item => item.id.toString()}
                contentContainerStyle={styles.scrollContent}
                refreshControl={
                  <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
                }
              />
              {renderPagination()}
            </>
          )}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  scrollContent: {
    padding: 16,
  },
  headerCard: {
    marginBottom: 16,
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)',
  },
  headerSubtitle: {
    color: 'rgb(90, 50, 180)',
    marginTop: 4,
  },
  activityCard: {
    marginBottom: 16,
    borderRadius: 8,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
  },
  activityHeader: {
    marginBottom: 8,
  },
  nameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  childName: {
    fontSize: 18,
    marginRight: 8,
    color: 'rgb(125, 76, 236)',
    fontWeight: 'bold',
  },
  activeBadge: {
    backgroundColor: 'rgb(125, 76, 236)',
  },
  activityType: {
    color: 'rgb(90, 50, 180)',
  },
  divider: {
    marginVertical: 12,
    backgroundColor: 'rgba(125, 76, 236, 0.2)',
  },
  infoContainer: {
    marginBottom: 16,
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  infoText: {
    marginLeft: 8,
    fontSize: 14,
    color: 'rgb(90, 50, 180)',
  },
  completeButton: {
    marginTop: 8,
    backgroundColor: 'rgb(125, 76, 236)',
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: 'rgb(125, 76, 236)',
  },
  errorContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  errorText: {
    marginTop: 16,
    fontSize: 16,
    color: 'rgb(125, 76, 236)',
    textAlign: 'center',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  emptyText: {
    marginTop: 16,
    marginBottom: 16,
    fontSize: 16,
    color: 'rgb(125, 76, 236)',
    textAlign: 'center',
  },
  createButton: {
    marginTop: 8,
    backgroundColor: 'rgb(125, 76, 236)',
  },
  // Modal Stilleri
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 10,
    width: '85%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
    borderWidth: 1,
    borderColor: 'rgba(125, 76, 236, 0.3)',
  },
  modalHeader: {
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(125, 76, 236, 0.3)',
    paddingVertical: 15,
    paddingHorizontal: 20,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)',
  },
  modalBody: {
    paddingVertical: 20,
    paddingHorizontal: 20,
  },
  modalText: {
    fontSize: 16,
    color: 'rgb(90, 50, 180)',
    lineHeight: 24,
  },
  modalFooter: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: 'rgba(125, 76, 236, 0.3)',
  },
  modalButton: {
    flex: 1,
    paddingVertical: 15,
    alignItems: 'center',
  },
  cancelButton: {
    borderRightWidth: 0.5,
    borderRightColor: 'rgba(125, 76, 236, 0.3)',
    backgroundColor: 'rgba(125, 76, 236, 0.1)',
    borderBottomLeftRadius: 10,
  },
  confirmButton: {
    borderLeftWidth: 0.5,
    borderLeftColor: 'rgba(125, 76, 236, 0.3)',
    backgroundColor: 'rgb(125, 76, 236)',
    borderBottomRightRadius: 10,
  },
  cancelButtonText: {
    color: 'rgb(125, 76, 236)',
    fontWeight: '600',
  },
  confirmButtonText: {
    color: 'white',
    fontWeight: '600',
  },
  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    elevation: 2,
    borderRadius: 8,
    marginHorizontal: 16,
    marginBottom: 16,
  },
  paginationButton: {
    margin: 0,
  },
  paginationButtonDisabled: {
    opacity: 0.5,
  },
  paginationText: {
    fontSize: 14,
    marginHorizontal: 12,
    color: 'rgb(125, 76, 236)',
    fontWeight: 'bold',
  },
});

export default ActiveParkActivitiesScreen;
